<script setup lang="ts">
import {computed, ref} from 'vue';
import type {WebEidAuthToken} from '@/features/auth/api/auth';
import type {
    WebEidCeremonyApi,
    WebEidCeremonyHandle,
    WebEidCeremonySuccess
} from '@/features/auth-ceremony/types';
import {useSettingsStore} from '@/store/settings';

defineOptions({name: 'WebEidCeremony'});

const props = withDefaults(defineProps<{
    translations: Record<string, string>;
    apiConfig: WebEidCeremonyApi;
    /** Optional pre-flight validation (login uses this to gate on email). Returning false halts. */
    preStart?: () => boolean | Promise<boolean>;
    /** Data-test id prefix used by the e2e suite. Login uses "web-eid", binding uses "bind-web-eid". */
    testIdPrefix?: string;
}>(), {
    preStart: undefined,
    testIdPrefix: 'web-eid'
});

const emit = defineEmits<{
    success: [payload: WebEidCeremonySuccess];
    errorCode: [code: string | null];
}>();

const errorCode = ref<string | null>(null);
const isWorking = ref(false);

declare global {
    interface Window {
        webeid?: {
            authenticate: (challengeNonce: string, lang: string) => Promise<WebEidAuthToken>;
        };
    }
}

function t(key: string, fallback?: string): string {
    const value = props.translations[key];
    if (value && value !== key.toLowerCase()) return value;
    return fallback ?? key;
}

const errorMessage = computed(() => {
    if (!errorCode.value) return '';
    if (errorCode.value === 'NetworkError') return t('auth-interror');
    if (errorCode.value === 'TooManyRequests') return t('lbl-too-many-login-requests');
    if (errorCode.value === 'AuthIdentityNotLinked') return t('lbl-auth-identity-not-linked');
    if (errorCode.value === 'SessionTimeoutException'
        || errorCode.value === 'WebEidChallengeNonceNotFound'
        || errorCode.value === 'PersonalCodeNotLinked') {
        return t('auth-smartid-sessiontimeout');
    }
    return t('auth-cert-validation-failed');
});

function mapWebEidErrorCode(err: unknown): string {
    const code = (err as {code?: string})?.code;
    if (typeof code === 'string') {
        if (code.includes('USER_CANCEL')) return 'ERR_WEBEID_USER_CANCELLED';
        if (code.includes('EXTENSION')) return 'ERR_WEBEID_EXTENSION_UNAVAILABLE';
        if (code.includes('NATIVE') || code.includes('APP_VERSION')) return 'ERR_WEBEID_NATIVE_UNAVAILABLE';
        if (code.includes('CARD') || code.includes('CERTIFICATE') || code.includes('PIN')) return 'ERR_WEBEID_CARD';
    }
    return 'ERR_WEBEID_CARD';
}

const settings = useSettingsStore();

async function start(): Promise<void> {
    if (isWorking.value) return;
    if (props.preStart && !(await props.preStart())) return;

    if (!window.webeid || typeof window.webeid.authenticate !== 'function') {
        errorCode.value = 'ERR_WEBEID_EXTENSION_UNAVAILABLE';
        emit('errorCode', errorCode.value);
        return;
    }

    errorCode.value = null;
    isWorking.value = true;
    try {
        const initiate = await props.apiConfig.initiate();

        if (initiate.retryAfterSeconds != null) {
            errorCode.value = 'TooManyRequests';
            emit('errorCode', errorCode.value);
            return;
        }
        if (initiate.networkError) {
            errorCode.value = 'NetworkError';
            emit('errorCode', errorCode.value);
            return;
        }
        if (!initiate.ok || !initiate.data?.nonce) {
            errorCode.value = initiate.errorCode ?? null;
            emit('errorCode', errorCode.value);
            return;
        }

        const lang = (settings.languageCode || 'et').slice(0, 2).toUpperCase();

        let authToken: WebEidAuthToken;
        try {
            authToken = await window.webeid.authenticate(initiate.data.nonce, lang);
        } catch (err) {
            errorCode.value = mapWebEidErrorCode(err);
            emit('errorCode', errorCode.value);
            return;
        }

        const completion = await props.apiConfig.complete(authToken);

        if (completion.retryAfterSeconds != null) {
            errorCode.value = 'TooManyRequests';
            emit('errorCode', errorCode.value);
            return;
        }
        if (completion.networkError) {
            errorCode.value = 'NetworkError';
            emit('errorCode', errorCode.value);
            return;
        }
        if (completion.ok && completion.data) {
            emit('success', {completion: completion.data});
            return;
        }
        errorCode.value = completion.errorCode ?? null;
        emit('errorCode', errorCode.value);
    } finally {
        isWorking.value = false;
    }
}

defineExpose<WebEidCeremonyHandle>({
    start,
    isBusy: () => isWorking.value
});
</script>

<template>
    <div class="web-eid-ceremony">
        <div v-if="errorMessage" class="error-div" :data-test="`${testIdPrefix}-error`">{{ errorMessage }}</div>
    </div>
</template>

<style scoped>
.web-eid-ceremony {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.error-div {
    color: #c1272d;
    min-height: 20px;
}
</style>
